import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type SessionResponseDto,
  type CreateSessionDto,
  type UpdateSessionDto,
} from "@tabipla/shared";

// Query keys
export const sessionKeys = {
  all: ["sessions"] as const,
  group: (groupId: string) => [...sessionKeys.all, groupId] as const,
  detail: (groupId: string, sessionId: string) =>
    [...sessionKeys.group(groupId), sessionId] as const,
};

// Fetch sessions for a group
export function useSessions(groupId: string | undefined) {
  return useQuery({
    queryKey: sessionKeys.group(groupId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<SessionResponseDto[]>(`/api/groups/${groupId}/sessions`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch sessions");
      }
      return response.data;
    },
    enabled: !!groupId,
  });
}

// Fetch single session
export function useSession(groupId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: sessionKeys.detail(groupId ?? "", sessionId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<SessionResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch session");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

// Create session mutation
export function useCreateSession(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (session: CreateSessionDto) => {
      const response = await apiClient.post<SessionResponseDto>(
        `/api/groups/${groupId}/sessions`,
        session
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create session");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
    },
  });
}

// Update session mutation
export function useUpdateSession(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (session: UpdateSessionDto) => {
      const response = await apiClient.put<SessionResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}`,
        session
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update session");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: sessionKeys.detail(groupId, sessionId) });
    },
  });
}

// Delete session mutation
export function useDeleteSession(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiClient.delete<null>(`/api/groups/${groupId}/sessions/${sessionId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete session");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
    },
  });
}

// Complete session mutation
export function useCompleteSession(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiClient.post<SessionResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/complete`,
        {}
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to complete session");
      }
      return response.data;
    },
    onSuccess: (_, sessionId) => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: sessionKeys.detail(groupId, sessionId) });
      // セッション完了で残高が変わるのでサマリーも更新
      queryClient.invalidateQueries({ queryKey: ["balanceSummary"] });
      queryClient.invalidateQueries({ queryKey: ["balance-summary"] });
    },
  });
}

// Reopen session mutation
export function useReopenSession(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiClient.post<SessionResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/reopen`,
        {}
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to reopen session");
      }
      return response.data;
    },
    onSuccess: (_, sessionId) => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: sessionKeys.detail(groupId, sessionId) });
      // セッション再開で残高が変わるのでサマリーも更新
      queryClient.invalidateQueries({ queryKey: ["balanceSummary"] });
      queryClient.invalidateQueries({ queryKey: ["balance-summary"] });
    },
  });
}
